import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { authAPI } from '@/lib/api';
import { Mail, Lock, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const LOGO_FULL = 'https://customer-assets.emergentagent.com/job_buyer-match-26/artifacts/zpw7rfj3_zynq_logo_full_4a2d15a6bb18411a903f9c9223055164.png';
const LOGO_ICON = 'https://customer-assets.emergentagent.com/job_buyer-match-26/artifacts/52px3160_zynq_logo_icon_e0dde9b0559f42738516da0422c8a0d0.png';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await authAPI.login({ email, password });
      login(res.data.token, res.data.user);
      toast.success(`Welkom terug, ${res.data.user.name}!`);
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Authenticatie mislukt');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left panel - branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-primary relative items-center justify-center overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 border border-white/20 rounded-full" />
          <div className="absolute bottom-32 right-16 w-96 h-96 border border-white/20 rounded-full" />
          <div className="absolute top-1/2 left-1/3 w-48 h-48 border border-white/20 rounded-full" />
        </div>
        <div className="relative z-10 text-white px-16 max-w-lg">
          <div className="flex items-center gap-3 mb-8">
            <img src={LOGO_FULL} alt="Zynq" className="h-12 brightness-0 invert" />
          </div>
          <h1 className="text-4xl font-bold tracking-tight leading-tight mb-4">
            Vitaliteit op de werkvloer, georganiseerd.
          </h1>
          <p className="text-white/70 text-lg leading-relaxed">
            Beheer leads, diensten en klantrelaties in een krachtig CRM, gebouwd voor vitaliteitsprofessionals.
          </p>
          <div className="mt-12 space-y-4">
            {['Leadbeheer & pipeline', 'Workshop & dienstplanning', 'Klantopvolging', 'Teammanagement'].map((f) => (
              <div key={f} className="flex items-center gap-3 text-white/80">
                <div className="w-1.5 h-1.5 rounded-full bg-white/60" />
                <span className="text-sm">{f}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel - form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-[400px] space-y-6">
          <div className="lg:hidden flex items-center gap-3 mb-4">
            <img src={LOGO_ICON} alt="Zynq" className="w-10 h-10 rounded-lg" />
            <span className="text-xl font-bold tracking-tight">Zynq</span>
          </div>

          <div>
            <h2 className="text-2xl font-bold tracking-tight">Welkom terug</h2>
            <p className="text-muted-foreground mt-1.5 text-sm">
              Log in met je gegevens
            </p>
          </div>

          <div className="space-y-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">E-mail</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    data-testid="login-email-input"
                    type="email"
                    placeholder="jij@bedrijf.nl"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-11"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Wachtwoord</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="password"
                    data-testid="login-password-input"
                    type="password"
                    placeholder="Voer je wachtwoord in"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 h-11"
                    required
                  />
                </div>
              </div>
              <Button
                type="submit"
                className="w-full h-11 gap-2"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? 'Even geduld...' : 'Inloggen'}
                <ArrowRight className="w-4 h-4" />
              </Button>
            </form>

            <p className="text-center text-xs text-muted-foreground">
              Geen account? Neem contact op met je beheerder.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
